// =============================================================================
// File: frontend/src/components/forms/LongevityPage.jsx
// Longevity Intelligence (#76) — personalized anti-aging engine.
// Tabs: Overview (ASSESS), Interventions (RANK), Simulate (MAX), Regimen, History.
// Patent claims exercised: P-014 (Personalized Longevity Intervention Engine).
// Ported faithfully from Longevity_Intelligence_Mockup.html. All styles live in
// /css/healist-longevity.css under the `lon-` namespace. No inline styles except
// JS-dependent values (driver bar widths, gauge stroke-dashoffset).
// =============================================================================

import React, { useState, useEffect, useCallback, useContext, useRef } from 'react';
import { API_BASE } from '../../config';
import { AuthContext } from '../../App';
import PaidGate from '../PaidGate';
import XaiPromptPanel from '../XaiPromptPanel';
import { CitationChip } from '../Citations';
import EvidenceWarnings from '../EvidenceWarnings';
import { handleAIError } from '../../utils/aiErrorHandler';

/**
 * G16 (Apple 1.4.1; Vincent 2026-10-18: "recommendations should come with both citations and
 * warnings"). Every longevity option shows, ALWAYS VISIBLE on its card, the shared EvidenceWarnings
 * pair (also used by AI Supplement Suggestions, F-15). Longevity keeps its own test ids.
 */
function LongevityEvidence({ item, safetySources = [], testId = 'longevity-citations' }) {
  return (
    <EvidenceWarnings item={item} safetySources={safetySources} featureName="Longevity Intelligence"
                      groupTitle="Evidence for this intervention" testId={testId}
                      warnTestId="longevity-warnings" noWarnTestId="longevity-no-warnings" />
  );
}

// Gauge geometry: r=84 → circumference ≈ 527 (2·π·84).
const GAUGE_CIRC = 527;
const gaugeOffset = (li) => GAUGE_CIRC * (1 - (Number(li) || 0) / 100);

// ROI score (0..100 or 0..5) → 5-dot meter.
const roiDots = (score) => {
  if (score == null) return 0;
  const n = Number(score);
  const onCount = n > 5 ? Math.round((n / 100) * 5) : Math.round(n);
  return Math.max(0, Math.min(5, onCount));
};

const TABS = [
  { key: 'overview', label: 'Overview', icon: '📊', free: true },
  { key: 'interventions', label: 'Interventions', icon: '🎯' },
  { key: 'simulate', label: 'Simulate', icon: '📈', max: true },
  { key: 'regimen', label: 'Regimen', icon: '🗓️' },
  { key: 'history', label: 'History', icon: '🕑' },
];

const DRIVER_META = {
  cardiometabolic: { icon: '❤️', name: 'Cardiometabolic' },
  inflammatory: { icon: '🔥', name: 'Inflammatory' },
  hormonal: { icon: '⚗️', name: 'Hormonal' },
  body_composition: { icon: '🧍', name: 'Body Composition' },
};
const DRIVER_ORDER = ['cardiometabolic', 'inflammatory', 'hormonal', 'body_composition'];

// All four model evidence tiers (strong|moderate|limited|emerging) — 'limited' was
// missing and fell to the wrong default; it shares the weak-evidence style (#consistency P2/F).
const EVIDENCE_CLASS = { strong: 'strong', moderate: 'moderate', emerging: 'emerging', limited: 'emerging' };
// Filter chip key -> exact DB domain value (no substring matching) (#consistency G).
const DOMAIN_VALUE = { bio: 'bio_physical', aes: 'aesthetic' };
const GATE_LABEL = {
  clear: '✅ Clear for you',
  caution: '⚠️ Caution — interaction or lab flag',
  contraindicated: '🚫 Filtered for safety',
};

const fmtNum = (v, dp = 1) =>
  v == null || Number.isNaN(Number(v)) ? '—' : Number(v).toFixed(dp).replace(/\.0$/, '');

// Friendly confidence label from the 0–1 score.
const confLabel = (c) => {
  const n = Number(c);
  if (!Number.isFinite(n)) return 'Unknown';
  return n >= 0.8 ? 'High' : n >= 0.5 ? 'Moderate' : 'Low';
};
// How many of the 9 PhenoAge lab markers are on file (data_completeness lists the missing).
const markersOnFile = (dc) => {
  const list = Array.isArray(dc) ? dc : (dc && Array.isArray(dc.data_completeness) ? dc.data_completeness : []);
  const missing = list.filter((x) => String(x).startsWith('lab:')).length;
  return Math.max(0, 9 - missing);
};

// =============================================================================
function LongevityInner({ fetchWithAuth, setActiveTab, user }) {
  // Single source of truth — backend-resolved effective_tier (#consistency P0/A).
  // The FE NEVER recomputes tier from raw fields. isMax gates the Max-only Simulate family.
  const tier = user?.effective_tier || 'pro';
  const isMax = tier === 'max';

  const [tab, setTab] = useState('overview');

  // Assessment / overview
  const [assessment, setAssessment] = useState(null);
  const [chain, setChain] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [assessing, setAssessing] = useState(false);
  const [assessError, setAssessError] = useState(null);
  const [openWhy, setOpenWhy] = useState({}); // driver Why? toggles

  // Interventions / ranking
  const [ranked, setRanked] = useState([]);
  // Apple 1.4.1 — XAI record of the ranking call. Served by BOTH the fresh rank and the
  // cached GET /assessment (migration 118), so the citations survive a page reload.
  const [rankPromptData, setRankPromptData] = useState(null);
  const [filtered, setFiltered] = useState([]);
  const [ranking, setRanking] = useState(false);
  const [rankError, setRankError] = useState(null);
  const [rankRun, setRankRun] = useState(false); // has rank been run at least once
  const [focusFilter, setFocusFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [openIWhy, setOpenIWhy] = useState({}); // intervention Why? toggles

  // Simulate
  const [picks, setPicks] = useState({});
  const [horizon, setHorizon] = useState(1825); // days; 5yr default
  const [simulating, setSimulating] = useState(false);
  const [simResult, setSimResult] = useState(null);
  const [simError, setSimError] = useState(null);
  // Scenario compare (Max — BREADTH)
  const [scenarios, setScenarios] = useState([]);
  const [comparing, setComparing] = useState(false);
  const [compareResult, setCompareResult] = useState(null);
  const [compareError, setCompareError] = useState(null);

  // Regimen / briefing
  const [briefing, setBriefing] = useState(null);
  const [briefingLoading, setBriefingLoading] = useState(false);
  const [briefError, setBriefError] = useState(null);

  // Credit balance (for low-balance / ≥4cr confirm)
  const [balance, setBalance] = useState(null);

  // Confirm modal
  const [confirm, setConfirm] = useState(null); // { title, cost, onRun }

  // ── helpers ────────────────────────────────────────────────────────────
  const creditsLeft = balance?.daily
    ? (balance.daily.remaining ?? 0) + (balance.purchased_credits ?? 0)
    : null;

  const loadBalance = useCallback(async () => {
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/billing/credits/balance`);
      if (res.ok) setBalance(await res.json());
    } catch (_) { /* non-fatal */ }
  }, [fetchWithAuth]);

  // ≥4cr OR low balance → confirm first; otherwise run immediately.
  const spend = (title, cost, run) => {
    const lowBalance = creditsLeft != null && creditsLeft <= cost + 2;
    if (cost >= 4 || lowBalance) {
      setConfirm({ title, cost, onRun: run });
    } else {
      run();
    }
  };

  // ── boot: restore last assessment ──────────────────────────────────────
  const loadAssessment = useCallback(async () => {
    setBootLoading(true);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/longevity/assessment`);
      if (res.ok) {
        const data = await res.json();
        if (data?.assessment) setAssessment(data.assessment);
        if (Array.isArray(data?.ranked)) setRanked(data.ranked);
        if (data?.prompt_data) setRankPromptData(data.prompt_data);
        if (Array.isArray(data?.filtered)) setFiltered(data.filtered);
        if (Array.isArray(data?.ranked) && data.ranked.length) setRankRun(true);
      }
    } catch (_) { /* leave empty → intro state */ } finally {
      setBootLoading(false);
    }
  }, [fetchWithAuth]);

  // Run the initial load exactly ONCE. fetchWithAuth (a prop) gets a new identity on
  // every parent re-render (e.g. the 60s credit-balance poll), so without this guard
  // the effect re-fires and re-fetches the assessment → Overview flashes/reloads.
  const didInit = useRef(false);
  useEffect(() => {
    if (didInit.current) return;
    didInit.current = true;
    loadAssessment();
    loadBalance();
  }, [loadAssessment, loadBalance]);

  // ── ASSESS (GET /biological-age, 3cr) ──────────────────────────────────
  const runAssess = useCallback(async () => {
    setAssessing(true);
    setAssessError(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/longevity/biological-age`);
      if (await handleAIError(res)) { setAssessing(false); return; }
      if (!res.ok) { setAssessError('We could not complete your assessment. Please try again.'); setAssessing(false); return; }
      const data = await res.json();
      setAssessment(data);
      setChain(data.chain || null);
      loadBalance();
    } catch (_) {
      setAssessError('Network error while assessing. Please try again.');
    } finally {
      setAssessing(false);
    }
  }, [fetchWithAuth, loadBalance]);

  // Assess / Re-assess are FREE since 2026-10-29 (B-CREDIT1 closeout): deterministic, no model call —
  // so they run directly, with no credit confirm.
  const goAssess = () => { setTab('overview'); runAssess(); };

  // ── RANK (POST /rank, Pro 2cr / Max full 4cr) ──────────────────────────
  const runRank = useCallback(async (fullEngine) => {
    setRanking(true);
    setRankError(null);
    setRankRun(true);
    try {
      const body = {};
      if (focusFilter !== 'all') body.focus_drivers = [focusFilter];
      if (domainFilter !== 'all') body.domains = [domainFilter];
      if (fullEngine) body.full_engine = true;
      const res = await fetchWithAuth(`${API_BASE}/api/longevity/rank`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (await handleAIError(res)) { setRanking(false); return; }
      if (!res.ok) { setRankError('We could not rank your interventions. Please try again.'); setRanking(false); return; }
      const data = await res.json();
      setRanked(Array.isArray(data.ranked) ? data.ranked : []);
      setRankPromptData(data.prompt_data || null);
      setFiltered(Array.isArray(data.filtered) ? data.filtered : []);
      loadBalance();
    } catch (_) {
      setRankError('Network error while ranking. Please try again.');
    } finally {
      setRanking(false);
    }
  }, [fetchWithAuth, focusFilter, domainFilter, loadBalance]);

  const triggerRank = () => spend('Rank my interventions?', 3, () => runRank(true));  // full engine for all tiers (#consistency P0/A)
  const goRank = () => { setTab('interventions'); triggerRank(); };

  // ── BRIEFING (POST /briefing, FREE — deterministic since F-4) ─────────
  const runBriefing = useCallback(async (keys) => {
    setBriefingLoading(true);
    setBriefError(null);
    setTab('regimen');
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/longevity/briefing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intervention_keys: keys || ranked.map((r) => r.intervention_key) }),
      });
      if (await handleAIError(res)) { setBriefingLoading(false); return; }
      if (!res.ok) { setBriefError('We could not build your briefing. Please try again.'); setBriefingLoading(false); return; }
      setBriefing(await res.json());
      loadBalance();
    } catch (_) {
      setBriefError('Network error while building briefing. Please try again.');
    } finally {
      setBriefingLoading(false);
    }
  }, [fetchWithAuth, ranked, loadBalance]);

  const triggerBrief = (key) => spend('Build clinical safety briefing?', 0, () => runBriefing(key ? [key] : null));

  // ── SIMULATE (POST /simulate, Max 4cr) ─────────────────────────────────
  const runSimulate = useCallback(async () => {
    setSimulating(true);
    setSimError(null);
    try {
      const keys = Object.keys(picks).filter((k) => picks[k]);
      const res = await fetchWithAuth(`${API_BASE}/api/longevity/simulate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ intervention_keys: keys, horizon_days: horizon }),
      });
      if (await handleAIError(res)) { setSimulating(false); return; }
      if (!res.ok) { setSimError('We could not run your simulation. Please try again.'); setSimulating(false); return; }
      setSimResult(await res.json());
      loadBalance();
    } catch (_) {
      setSimError('Network error while simulating. Please try again.');
    } finally {
      setSimulating(false);
    }
  }, [fetchWithAuth, picks, horizon, loadBalance]);

  const triggerSimulate = () => spend('Run simulation?', 4, runSimulate);

  // ── SCENARIO COMPARE (POST /scenarios/compare, Max, 2cr) ────────────────
  const saveScenario = () => {
    const keys = Object.keys(picks).filter((k) => picks[k]);
    if (!keys.length) return;
    setScenarios((s) => [...s, { label: `Scenario ${String.fromCharCode(65 + s.length)}`, intervention_keys: keys }]);
  };
  const runCompare = useCallback(async () => {
    setComparing(true); setCompareError(null); setCompareResult(null);
    try {
      const res = await fetchWithAuth(`${API_BASE}/api/longevity/scenarios/compare`, {
        method: 'POST',
        body: JSON.stringify({ scenarios, horizon_days: horizon }),
      });
      if (await handleAIError(res)) { setComparing(false); return; }
      if (!res.ok) { setCompareError('Scenario comparison failed. Please try again.'); setComparing(false); return; }
      setCompareResult(await res.json());
      loadBalance();
    } catch {
      setCompareError('Network error during comparison. Please try again.');
    } finally { setComparing(false); }
  }, [fetchWithAuth, scenarios, horizon, loadBalance]);
  const triggerCompare = () => { if (scenarios.length >= 2) spend('Compare scenarios?', 2, runCompare); };

  // ── derived ────────────────────────────────────────────────────────────
  const delta = assessment?.delta_years;
  const olderClass = delta != null && Number(delta) > 0 ? ' older' : '';
  const drivers = assessment?.drivers || {};
  const chainData = chain || assessment?.chain || null;

  const freshLabel = assessing
    ? 'Assessing…'
    : assessment
      ? (assessment.is_stale
          ? 'Out of date — re-assess'
          : `Assessed${assessment.computed_at ? ` ${new Date(assessment.computed_at).toLocaleDateString()}` : ''}`)
      : 'Not yet assessed';
  const tierLabel = isMax ? 'Longevity • Optimize' : 'Longevity • Know';

  const visibleRanked = ranked.filter((r) => {
    const okFocus = focusFilter === 'all' || (r.targets || []).some((t) => String(t).toLowerCase().includes(focusFilter)) || (r.subdomain || '').toLowerCase().includes(focusFilter);
    // Explicit domain values — no fragile substring matching (#consistency G).
    const okDomain = domainFilter === 'all' || (r.domain || '') === DOMAIN_VALUE[domainFilter];
    return okFocus && okDomain;
  });

  const isAesthetic = (d) => (d || '') === 'aesthetic';

  // ── render ─────────────────────────────────────────────────────────────
  return (
    <div className="page-layout lon-root">
      {/* D4S sticky nav: standard header row + tab bar */}
      <div className="page-sticky-nav">
        <div className="page-header-row">
          <h2 className="page-section-title">Longevity Intelligence</h2>
          <button className="page-exit-btn" onClick={() => setActiveTab('dashboard')}>&larr; Dashboard</button>
        </div>
        <div className="page-nav-bar">
          {/* Tab bar — navigation is FREE */}
          <div className="lon-tabbar" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            role="tab"
            aria-selected={tab === t.key}
            className={`lon-tab${tab === t.key ? ' active' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.icon} {t.label}
            {t.free && <span className="lon-freeTag">FREE</span>}
            {t.max && !isMax && <span className="lon-maxLock">MAX</span>}
          </button>
        ))}
          </div>
        </div>
      </div>

      {/* Scrollable content area (D4S) */}
      <div className="page-scroll-content lon-content">
        <div className="lon-statusRow">
          <p className="lon-subtitle">Your personalized anti-aging engine — assess, rank, simulate, optimize. Advice only; confirm everything with your provider.</p>
          <div className="lon-statusChips">
            <span className="lon-freshnessChip"><span className="lon-dot" /> {freshLabel}</span>
            <span className="lon-tierPillLg">✦ {tierLabel}</span>
          </div>
        </div>

      {/* ═══════════ OVERVIEW (ASSESS) ═══════════ */}
      {tab === 'overview' && (
        <section className="lon-panel" role="tabpanel">
          {assessError && !assessing && (
            <div className="lon-errorState">
              <div className="lon-ico">⚠️</div>
              <h3>Assessment failed</h3>
              <p>{assessError}</p>
              <button className="lon-btn lon-btnPrimary" onClick={runAssess}>
                Try again <span className="lon-freeChip">FREE</span>
              </button>
            </div>
          )}

          {!assessError && bootLoading && (
            <div className="lon-loadingState"><div className="lon-spinner" /><h3>Loading your longevity profile…</h3></div>
          )}

          {!assessError && !bootLoading && assessing && (
            <div className="lon-loadingState">
              <div className="lon-spinner" />
              <h3>Assessing your biological age…</h3>
              <p>Reading your Unified Health Context, decomposing aging drivers, and grounding against your Digital Twin.</p>
              <div className="lon-steps">UHC · PhenoAge method{assessment?.method_version ? ` ${assessment.method_version}` : ''}</div>
            </div>
          )}

          {/* Empty / intro */}
          {!assessError && !bootLoading && !assessing && !assessment && (
            <div className="lon-introState">
              <div className="lon-ico">✨</div>
              <h3>Discover your biological age</h3>
              <p>Longevity Intelligence builds a clinical picture of how your body is actually aging — from your labs, body composition, and device data — then ranks the highest-ROI ways to bend the curve.</p>
              <div className="lon-introChecklist">
                <div className="lon-ci"><span className="lon-tick">✓</span> Lab panel — PhenoAge markers on file</div>
                <div className="lon-ci"><span className="lon-tick">✓</span> Body composition (BIA)</div>
                <div className="lon-ci"><span className="lon-tick">✓</span> Device data — VO₂max, resting HR, sleep</div>
                <div className="lon-ci"><span className="lon-circle">○</span> Missing markers will be noted in your confidence score</div>
              </div>
              <button className="lon-btn lon-btnPrimary" onClick={runAssess}>
                Assess my biological age
                <span className="lon-freeChip">FREE</span>
              </button>
              <div className="lon-introNote">Your assessment is free. Credits are spent only on ranking interventions and simulations.</div>
            </div>
          )}

          {/* Result */}
          {!assessError && !bootLoading && !assessing && assessment && (
            <>
              {assessment.is_stale && (
                <div className="lon-recalBanner">
                  <span className="lon-ico">🔄</span>
                  <div className="lon-txt">
                    <b>Your health data changed.</b>
                    <small>New data added — your assessment may be out of date. <span className="lon-freeNote">Detecting changes and re-assessing are free.</span></small>
                  </div>
                  <button className="lon-btn lon-btnPrimary lon-btnSm" onClick={runAssess}>
                    Re-assess <span className="lon-freeChip">FREE</span>
                  </button>
                </div>
              )}

              {/* Bio-age hero */}
              <div className="lon-card">
                <div className="lon-hero">
                  <div>
                    <div className="lon-heroLabel">Biological Age</div>
                    <div className="lon-heroNum">{fmtNum(assessment.biological_age)}</div>
                    {delta != null && (
                      <div className={`lon-deltaChip${olderClass}`}>
                        {Number(delta) <= 0
                          ? `▾ ${fmtNum(delta)} yrs · younger than your calendar age of ${fmtNum(assessment.chronological_age, 0)}`
                          : `▴ +${fmtNum(Math.abs(delta))} yrs · older than your calendar age of ${fmtNum(assessment.chronological_age, 0)}`}
                      </div>
                    )}
                    <div className="lon-confidenceRow">
                      <span className="lon-confBadge">
                        Confidence: <b>{confLabel(assessment.confidence)}</b>
                        {` · ${markersOnFile(assessment.data_completeness)} of 9 lab markers on file`}
                      </span>
                      <a className="lon-confLink" onClick={() => setTab('history')}>Improve accuracy →</a>
                    </div>
                  </div>
                  <div className="lon-gaugeWrap">
                    <div className="lon-gauge">
                      <svg width="200" height="200" viewBox="0 0 200 200">
                        <circle cx="100" cy="100" r="84" fill="none" stroke="#E5E7EB" strokeWidth="16" />
                        <defs>
                          <linearGradient id="lonGoldArc" x1="0" y1="0" x2="1" y2="1">
                            <stop offset="0%" stopColor="#C9A962" />
                            <stop offset="100%" stopColor="#E8D5A3" />
                          </linearGradient>
                        </defs>
                        <circle
                          cx="100" cy="100" r="84" fill="none" stroke="url(#lonGoldArc)" strokeWidth="16"
                          strokeLinecap="round" strokeDasharray={GAUGE_CIRC}
                          strokeDashoffset={gaugeOffset(assessment.longevity_index)}
                        />
                      </svg>
                      <div className="lon-gaugeCenter">
                        <div className="lon-gaugeVal">{fmtNum(assessment.longevity_index, 0)}</div>
                        <div className="lon-gaugeLbl">Longevity Index</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Metric chain HS → LI → BA */}
              <div className="lon-metricChain">
                <div className="lon-metricChainHead">
                  <span style={{ fontSize: '20px' }}>🧭</span>
                  <h3>How your three numbers fit together</h3>
                  <span className="lon-sub">One chain: today → trajectory → your age</span>
                </div>
                <div className="lon-chainRow">
                  <div className="lon-chainNode">
                    <div className="lon-cnVal">{fmtNum(chainData?.healist_score?.value ?? assessment.healist_score, 0)}</div>
                    <div className="lon-cnName">{chainData?.healist_score?.label || 'Healist Score'}</div>
                    <div className="lon-cnQ">"{chainData?.healist_score?.question || "How's my health right now?"}"</div>
                    <div className="lon-cnWhere">{chainData?.healist_score?.where || 'Main dashboard · daily'}</div>
                  </div>
                  <div className="lon-chainArrow">→</div>
                  <div className="lon-chainNode">
                    <div className="lon-cnVal">{fmtNum(chainData?.longevity_index?.value ?? assessment.longevity_index, 0)}<small>/100</small></div>
                    <div className="lon-cnName">{chainData?.longevity_index?.label || 'Longevity Index'}</div>
                    <div className="lon-cnQ">"{chainData?.longevity_index?.question || 'How optimized is my aging trajectory?'}"</div>
                    <div className="lon-cnWhere">{chainData?.longevity_index?.where || 'Longevity · the movable dial'}</div>
                  </div>
                  <div className="lon-chainArrow">→</div>
                  <div className="lon-chainNode">
                    <div className="lon-cnVal">{fmtNum(chainData?.biological_age?.value ?? assessment.biological_age)}<small>yrs</small></div>
                    <div className="lon-cnName">{chainData?.biological_age?.label || 'Biological Age'}</div>
                    <div className="lon-cnQ">"{chainData?.biological_age?.question || "What's my physiological age?"}"</div>
                    <div className="lon-cnWhere">{chainData?.biological_age?.where || 'Longevity · the slow anchor'}</div>
                  </div>
                </div>
                <div className="lon-chainLinkNote">
                  <span>
                    <span className="lon-lever">Longevity Index</span> is the leading dial you can move now; <span className="lon-lever">Biological Age</span> is the slower clinical outcome it pulls. <span className="lon-muted">(Your Healist Score lives on the main dashboard — the daily-health view. LI &amp; BA are the longevity lens.)</span>
                  </span>
                  <span className="lon-maxTag">Max · simulated</span>
                  <span className="lon-simLine">Raise your Longevity Index ⇒ a lower biological age over time.</span>
                </div>
              </div>

              {/* Drivers */}
              <h2 className="lon-blockTitle">What's driving your aging</h2>
              <p className="lon-blockSub">Decomposition of your biological age across four domains, grounded in your Unified Health Context + Digital Twin.</p>
              <div className="lon-driverGrid">
                {DRIVER_ORDER.map((key) => {
                  const d = drivers[key];
                  if (!d) return null;
                  const meta = DRIVER_META[key];
                  const fav = d.favorable === true;
                  const hasData = d.score != null;
                  const barWidth = Math.max(8, Math.min(100, Number(d.score) || 0));
                  const statusLabel = d.status === 'strong' ? 'Strong'
                    : d.status === 'moderate' ? 'Moderate'
                    : d.status === 'needs_attention' ? 'Needs attention' : '—';
                  const contribs = Array.isArray(d.contributors) ? d.contributors : [];
                  const why = openWhy[key];
                  return (
                    <div className="lon-driverCard" key={key}>
                      <div className="lon-driverHead">
                        <span className="lon-driverIcon">{meta.icon}</span>
                        <span className="lon-driverName">{meta.name}</span>
                      </div>
                      <div className="lon-driverBarTrack">
                        <div className={`lon-driverBar ${fav ? 'fav' : 'unfav'}`} style={{ width: `${barWidth}%` }} />
                      </div>
                      <div className={`lon-driverContrib ${fav ? 'fav' : 'unfav'}`}>
                        {hasData ? `${statusLabel} · score ${Math.round(Number(d.score))}/100` : 'No data on file yet'}
                      </div>
                      {contribs.length > 0 && (
                        <div className="lon-driverMarkers">
                          {contribs.map((c, i) => (
                            <span key={i}>{c.label} {c.value}{i < contribs.length - 1 ? ' · ' : ''}</span>
                          ))}
                        </div>
                      )}
                      <div className="lon-whyToggle" onClick={() => setOpenWhy((p) => ({ ...p, [key]: !p[key] }))}>
                        Why? <span className="lon-freeChip">FREE</span> {why ? '▴' : '▾'}
                      </div>
                      {why && (
                        <div className="lon-whyBody">
                          {contribs.length > 0
                            ? `This driver scores ${Math.round(Number(d.score))}/100 (${statusLabel.toLowerCase()}), from ${contribs.map((c) => `${c.label} ${c.value}`).join(', ')}. The lower-scoring markers are your biggest opportunities — they map to your ranked interventions.`
                            : 'No data on file for this driver yet — connect labs or device data to populate it.'}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* ASSESS → RANK handoff */}
              <div className="lon-ctaBand">
                <button className="lon-btn lon-btnPrimary" onClick={goRank}>
                  Rank my interventions
                  <span className="lon-costChip"><span className="lon-gem">⟡</span> {3}</span>
                  →
                </button>
                <button className="lon-btn lon-btnSecondary" onClick={() => setTab('regimen')}>
                  View Last Regimen <span className="lon-freeChip">FREE</span>
                </button>
              </div>
            </>
          )}
        </section>
      )}

      {/* ═══════════ INTERVENTIONS (RANK) ═══════════ */}
      {tab === 'interventions' && (
        <section className="lon-panel" role="tabpanel">
          {rankError && !ranking && (
            <div className="lon-errorState">
              <div className="lon-ico">⚠️</div>
              <h3>Ranking failed</h3>
              <p>{rankError}</p>
              <button className="lon-btn lon-btnPrimary" onClick={triggerRank}>
                Try again <span className="lon-costChip"><span className="lon-gem">⟡</span> {3}</span>
              </button>
            </div>
          )}

          {!rankError && ranking && (
            <div className="lon-loadingState">
              <div className="lon-spinner" />
              <h3>Ranking your cross-domain engine…</h3>
              <p>Scoring ROI across bio-physical and aesthetic options, then running each through your safety gate (meds · labs · conditions).</p>
              <div className="lon-steps">Safety bridge · food-drug pharmacology engine · DOP-sourced</div>
            </div>
          )}

          {/* Empty / intro */}
          {!rankError && !ranking && !rankRun && ranked.length === 0 && (
            <div className="lon-introState">
              <div className="lon-ico">🎯</div>
              <h3>Rank your interventions</h3>
              <p>Score every option — bio-physical <em>and</em> aesthetic — on one anti-aging ROI scale, each gated against your medications, labs, and conditions. We surface the highest-leverage moves and show exactly what we filtered out and why.</p>
              <button className="lon-btn lon-btnPrimary" onClick={triggerRank}>
                Rank my interventions
                <span className="lon-costChip"><span className="lon-gem">⟡</span> {3}</span>
              </button>
              <div className="lon-introNote">A {3}-credit action. Filtering by focus or domain afterward is free.</div>
            </div>
          )}

          {/* Result */}
          {!rankError && !ranking && (rankRun || ranked.length > 0) && (
            <>
              <h2 className="lon-blockTitle">Your ranked interventions</h2>
              <p className="lon-blockSub">Every option — bio-physical and aesthetic — scored on <b>one anti-aging ROI scale</b> and gated against your medications, labs, and conditions.</p>

              <div className="lon-controlsRow">
                <div className="lon-filterGroup">
                  <span className="lon-lbl">Focus</span>
                  {[['all', 'All'], ['cardiometabolic', 'Cardiometabolic'], ['inflammatory', 'Inflammatory'], ['hormonal', 'Hormonal'], ['body', 'Body Comp']].map(([k, label]) => (
                    <span key={k} className={`lon-chip${focusFilter === k ? ' active' : ''}`} onClick={() => setFocusFilter(k)}>{label}</span>
                  ))}
                </div>
                <div className="lon-filterGroup">
                  <span className="lon-lbl">Domain</span>
                  {[['all', 'All'], ['bio', 'Bio-Physical'], ['aes', 'Aesthetic']].map(([k, label]) => (
                    <span key={k} className={`lon-chip${domainFilter === k ? ' active' : ''}`} onClick={() => setDomainFilter(k)}>{label}</span>
                  ))}
                </div>
                <div className="lon-reRankRow">
                  <span className="lon-freeNote">Filtering is free</span>
                  <button className="lon-btn lon-btnSecondary lon-btnSm" onClick={triggerRank}>
                    Re-rank <span className="lon-costChip"><span className="lon-gem">⟡</span> {3}</span>
                  </button>
                </div>
              </div>

              <div className="lon-gateLegend">
                <span className="lon-item">✅ Clear for you</span>
                <span className="lon-item">⚠️ Caution — interaction or lab flag</span>
                <span className="lon-item">🚫 Filtered for safety</span>
                <span className="lon-note">Safety checks run against your medications, labs, and conditions. Always confirm with your provider.</span>
              </div>

              {visibleRanked.map((iv, idx) => {
                const dots = roiDots(iv.roi_score);
                const ev = EVIDENCE_CLASS[(iv.evidence_tier || '').toLowerCase()] || 'moderate';
                const gate = (iv.gate_status || 'clear').toLowerCase();
                const why = openIWhy[iv.intervention_key || idx];
                return (
                  <div className="lon-irow" key={iv.intervention_key || idx}>
                    <div className="lon-irowTop">
                      <div className="lon-rankBadge">{idx + 1}</div>
                      <div className="lon-irowMain">
                        <div className="lon-irowTitle">
                          {iv.name}
                          <span className={`lon-domainTag ${isAesthetic(iv.domain) ? 'aes' : 'bio'}`}>
                            {isAesthetic(iv.domain) ? 'Aesthetic' : 'Bio-Physical'}{iv.subdomain ? ` · ${iv.subdomain}` : ''}
                          </span>
                        </div>
                        <div className="lon-irowMeta">
                          {Array.isArray(iv.targets) && iv.targets.length > 0 && (
                            <span>Targets: <b>{iv.targets.join(', ')}</b></span>
                          )}
                          {iv.evidence_tier && (
                            <span>Evidence: <span className={`lon-evidenceChip ${ev}`}>{iv.evidence_tier}</span></span>
                          )}
                          {iv.projected_bio_age_years != null && (
                            <span>Projected: <b>{fmtNum(iv.projected_bio_age_years)} yrs</b></span>
                          )}
                        </div>
                        {/* G16 — the card's evidence + authority warnings, always visible (one chip per card). */}
                        <LongevityEvidence
                          item={iv}
                          safetySources={(iv.reasons || []).flatMap(r => r.sources || (r.source_citation ? [r.source_citation] : []))}
                        />
                      </div>
                      <div className="lon-roi">
                        <div className="lon-roiDots">
                          {[0, 1, 2, 3, 4].map((i) => <span key={i} className={`lon-roiDot${i < dots ? ' on' : ''}`} />)}
                        </div>
                        <div className="lon-roiLbl">ROI</div>
                      </div>
                    </div>
                    <div className="lon-gateStatus">
                      <span className={`lon-gatePill ${gate === 'caution' ? 'caution' : 'clear'}`}>
                        {gate === 'caution'
                          ? `⚠️ Caution${iv.reasons?.[0]?.matched_on ? ` — ${iv.reasons[0].matched_on}` : ''}`
                          : '✅ Clear for you'}
                      </span>
                      <div className="lon-irowActions">
                        <button className="lon-linkBtn" onClick={() => setOpenIWhy((p) => ({ ...p, [iv.intervention_key || idx]: !p[iv.intervention_key || idx] }))}>
                          Why? <span className="lon-freeChip">FREE</span> {why ? '▴' : '▾'}
                        </button>
                        {gate === 'caution' && (
                          <button className="lon-linkBtn" onClick={() => triggerBrief(iv.intervention_key)}>
                            Brief <span className="lon-freeChip">FREE</span>
                          </button>
                        )}
                      </div>
                    </div>
                    {why && (
                      <div className="lon-irowWhy">
                        {iv.description && <div style={{ marginBottom: iv.rationale ? '8px' : 0, fontStyle: 'italic' }}>{iv.description}</div>}
                        {iv.rationale}
                        {Array.isArray(iv.reasons) && iv.reasons.length > 0 && (
                          // The sources of these safety reasons are in the card's Citations chip
                          // above (G16: one chip per card, always visible — not hidden behind Why?).
                          <ul style={{ margin: '8px 0 0', paddingLeft: '18px' }}>
                            {iv.reasons.map((r, i) => (
                              <li key={i}><b>{r.severity}:</b> {r.mechanism}{r.guidance ? ` — ${r.guidance}` : ''}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}

              {visibleRanked.length === 0 && ranked.length > 0 && (
                <p className="lon-blockSub">No interventions match the current filters.</p>
              )}

              {/* Apple 1.4.1 — the longevity plan is AI-ranked health guidance and must show its
                  reasoning and citations, in the same panel and the same place as every other AI
                  surface. featureName selects the baseline citation set in xaiHelpers. */}
              {rankPromptData && (
                <XaiPromptPanel
                  promptData={rankPromptData}
                  featureName="Longevity Intelligence"
                />
              )}

              {/* Filtered for safety */}
              {filtered.length > 0 && (
                <div className="lon-filteredSection">
                  <h3 className="lon-blockTitle">Filtered for your safety</h3>
                  <p className="lon-blockSub">We don't silently hide options — here's exactly what we excluded and why.</p>
                  {filtered.map((f, i) => (
                    <div className="lon-filteredRow" key={f.intervention_key || i}>
                      <span className="lon-ico">🚫</span>
                      <div>
                        <span className="lon-name">{f.name}</span>
                        <div className="lon-reason">
                          {f.reasons?.[0]?.severity && <b>{f.reasons[0].severity}: </b>}
                          {f.reasons?.[0]?.mechanism || f.reasons?.[0]?.guidance || 'Filtered for your safety.'}
                        </div>
                        <LongevityEvidence
                          item={f}
                          safetySources={(f.reasons || []).flatMap(r => r.sources || (r.source_citation ? [r.source_citation] : []))}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* (Removed the Pro "top shortlist → upgrade" teaser — the full ranked
                  engine is now shown to BOTH Pro and Max. #consistency P0/A) */}

              {/* 3-axis Pro vs Max comparison */}
              <div className="lon-axisCompare">
                <div className="lon-axisCompareHead">
                  <span style={{ fontSize: '20px' }}>⚖️</span>
                  <h3>Know vs Optimize — three ways Max goes further</h3>
                  <span className="lon-sub">It's not just simulation</span>
                </div>
                <table className="lon-axisTable">
                  <thead>
                    <tr>
                      <th>Axis</th>
                      <th>Longevity • Know <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(Pro)</span></th>
                      <th className="colMax">Longevity • Optimize <span style={{ fontWeight: 500, textTransform: 'none', letterSpacing: 0 }}>(Max)</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td><div className="lon-axisAxis">Foresight <small>where you're headed</small></div></td>
                      <td className="lon-axisCell pro"><span className="lon-axisIcon">📍</span> Your <b>current</b> assessment — bio-age, drivers, today's standing.</td>
                      <td className="lon-axisCell max"><span className="lon-axisIcon">📈</span> <b>Digital-Twin simulation</b> — bend your aging curve 5/10/20 yrs out; the quantified LI → BA link.</td>
                    </tr>
                    <tr>
                      <td><div className="lon-axisAxis">Breadth <small>how much you see</small></div></td>
                      <td className="lon-axisCell pro"><span className="lon-axisIcon">🗂️</span> The <b>complete ranked engine</b> — every option across domains, filterable.</td>
                      <td className="lon-axisCell max"><span className="lon-axisIcon">⚖️</span> Everything in Know, <b>plus scenario compare</b> — multiple regimens side-by-side.</td>
                    </tr>
                    <tr>
                      <td><div className="lon-axisAxis">Cadence <small>how often it refreshes</small></div></td>
                      <td className="lon-axisCell pro"><span className="lon-axisIcon">✋</span> <b>Manual re-assess</b> on demand when your data changes.</td>
                      <td className="lon-axisCell max"><span className="lon-axisIcon">🔄</span> <b>Continuous auto-recalibration</b> — detects new labs/Rx, re-simulates, alerts you.</td>
                    </tr>
                  </tbody>
                </table>
                <div className="lon-axisFoot">
                  <span><b>Pro</b> = where you stand + your best moves, now. &nbsp; <b>Max</b> = the full engine, your future simulated, never stops.</span>
                  {!isMax && (
                    <button className="lon-btn lon-btnGold lon-btnSm" style={{ marginLeft: 'auto' }} onClick={() => setActiveTab('account')}>Upgrade to Optimize →</button>
                  )}
                </div>
              </div>
            </>
          )}
        </section>
      )}

      {/* ═══════════ SIMULATE (MAX) ═══════════ */}
      {tab === 'simulate' && (
        <section className="lon-panel" role="tabpanel">
          <h2 className="lon-blockTitle">Biological-age trajectory simulation</h2>
          <p className="lon-blockSub">Project your selected plan onto your aging curve over time, powered by your Digital Twin.</p>

          {!isMax ? (
            <div className="lon-maxGate">
              <div className="lon-ico">📈</div>
              <h3>See your biological age 5, 10, 20 years out</h3>
              <p>Compare any plan you choose, side by side — available with <b>Longevity • Optimize</b>.</p>
              <button className="lon-btn lon-btnGold" onClick={() => setActiveTab('account')}>
                Unlock Simulation
                <span className="lon-costChip"><span className="lon-gem">⟡</span> 4</span>
              </button>
            </div>
          ) : (
            <>
              <div className="lon-simBuilder">
                <div className="lon-simCol lon-card lon-cardPad">
                  <h4>Build your scenario</h4>
                  {(ranked.length ? ranked : []).map((iv) => {
                    const sel = !!picks[iv.intervention_key];
                    const gate = (iv.gate_status || 'clear').toLowerCase();
                    return (
                      <div
                        key={iv.intervention_key}
                        className={`lon-pickItem${sel ? ' sel' : ''}`}
                        onClick={() => setPicks((p) => ({ ...p, [iv.intervention_key]: !p[iv.intervention_key] }))}
                      >
                        <span className="lon-chk">{sel ? '✓' : ''}</span> {iv.name}
                        <span className="lon-gateMini">{gate === 'caution' ? '⚠️' : '✅'}</span>
                      </div>
                    );
                  })}
                  {ranked.length === 0 && <p className="lon-blockSub">Rank your interventions first to build a scenario.</p>}
                </div>
                <div className="lon-simCol lon-card lon-cardPad">
                  <h4>Horizon</h4>
                  <div className="lon-horizonRow">
                    {[['1 yr', 365], ['5 yr', 1825], ['10 yr', 3650], ['20 yr', 7300]].map(([label, days]) => (
                      <span key={days} className={`lon-horizonChip${horizon === days ? ' active' : ''}`} onClick={() => setHorizon(days)}>{label}</span>
                    ))}
                  </div>
                  <div style={{ marginTop: '24px' }}>
                    <button className="lon-btn lon-btnPrimary" disabled={Object.values(picks).filter(Boolean).length === 0} onClick={triggerSimulate}>
                      Simulate this plan
                      <span className="lon-costChip"><span className="lon-gem">⟡</span> 4</span>
                    </button>
                    <div className="lon-introNote">A 4-credit action — we'll confirm before spending.</div>
                  </div>
                </div>
              </div>

              {simError && !simulating && (
                <div className="lon-errorState">
                  <div className="lon-ico">⚠️</div>
                  <h3>Simulation failed</h3>
                  <p>{simError}</p>
                  <button className="lon-btn lon-btnPrimary" onClick={triggerSimulate}>Try again</button>
                </div>
              )}

              {simulating && (
                <div className="lon-loadingState">
                  <div className="lon-spinner" />
                  <h3>Running your trajectory simulation…</h3>
                  <p>Projecting your selected plan onto your aging curve via your Digital Twin, with a confidence band.</p>
                  <div className="lon-steps">Digital Twin · predictive simulation · P-011</div>
                </div>
              )}

              {!simulating && simResult && (
                <div className="lon-genResult">
                  <h4>Simulation result</h4>
                  {simResult.explanation && <p className="lon-genPre" style={{ marginBottom: '12px' }}>{simResult.explanation}</p>}
                  {simResult.predictions && typeof simResult.predictions === 'object' ? (
                    Object.entries(simResult.predictions).map(([k, v]) => (
                      <div className="lon-genKv" key={k}>
                        <span className="lon-k">{k}</span>
                        <span className="lon-v">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span>
                      </div>
                    ))
                  ) : (
                    <pre className="lon-genPre">{JSON.stringify(simResult, null, 2)}</pre>
                  )}
                  <div className="lon-chartCallout" style={{ marginTop: '16px' }}>
                    We'll track this projection against your real labs over time and refine it (predicted-vs-actual calibration).
                  </div>
                </div>
              )}

              {/* SCENARIO COMPARE — BREADTH (Max) */}
              <div className="lon-card lon-cardPad" style={{ marginTop: '20px' }}>
                <h4>Compare scenarios</h4>
                <p className="lon-blockSub">Save two or more plans (build a selection above, then save), and diff their projected biological-age trajectories side by side.</p>
                <div className="lon-controlsRow" style={{ marginTop: '8px' }}>
                  <button className="lon-btn lon-btnSecondary lon-btnSm" disabled={Object.values(picks).filter(Boolean).length === 0} onClick={saveScenario}>+ Save current selection</button>
                  <button className="lon-btn lon-btnPrimary lon-btnSm" disabled={scenarios.length < 2} onClick={triggerCompare}>
                    Compare {scenarios.length || ''} scenarios <span className="lon-costChip"><span className="lon-gem">⟡</span> 2</span>
                  </button>
                </div>
                {scenarios.length > 0 && (
                  <div className="lon-driverMarkers" style={{ marginTop: '10px' }}>
                    {scenarios.map((s, i) => (
                      <span key={i} className="lon-chip">{s.label} ({s.intervention_keys.length}){i < scenarios.length - 1 ? '  ' : ' '}</span>
                    ))}
                    <button className="lon-linkBtn" onClick={() => { setScenarios([]); setCompareResult(null); }}>Clear</button>
                  </div>
                )}
                {comparing && <div className="lon-loadingState"><div className="lon-spinner" /><h3>Comparing scenarios…</h3></div>}
                {compareError && !comparing && <p className="lon-blockSub">{compareError}</p>}
                {!comparing && compareResult && Array.isArray(compareResult.scenarios) && (
                  <div className="lon-simBuilder" style={{ marginTop: '12px' }}>
                    {compareResult.scenarios.map((sc, i) => (
                      <div className="lon-simCol lon-card lon-cardPad" key={i}>
                        <h4>{sc.label || `Scenario ${i + 1}`}</h4>
                        {sc.simulation?.explanation && <p className="lon-blockSub">{sc.simulation.explanation}</p>}
                        {sc.simulation?.predictions && typeof sc.simulation.predictions === 'object'
                          ? Object.entries(sc.simulation.predictions).slice(0, 6).map(([k, v]) => (
                              <div className="lon-genKv" key={k}><span className="lon-k">{k}</span><span className="lon-v">{typeof v === 'object' ? JSON.stringify(v) : String(v)}</span></div>
                            ))
                          : <pre className="lon-genPre" style={{ maxHeight: '180px' }}>{JSON.stringify(sc.simulation, null, 2)}</pre>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </section>
      )}

      {/* ═══════════ REGIMEN ═══════════ */}
      {tab === 'regimen' && (
        <section className="lon-panel" role="tabpanel">
          <h2 className="lon-blockTitle">Your regimen</h2>
          <p className="lon-blockSub">What to discuss with your provider. Doses come from a cited source or your clinician — never from the AI. Names specific options; never ordering, booking, or payment.</p>

          {briefError && !briefingLoading && (
            <div className="lon-errorState">
              <div className="lon-ico">⚠️</div>
              <h3>Briefing failed</h3>
              <p>{briefError}</p>
            </div>
          )}

          {briefingLoading && (
            <div className="lon-loadingState"><div className="lon-spinner" /><h3>Building your regimen &amp; safety briefing…</h3></div>
          )}

          {!briefingLoading && !briefing && (
            <div className="lon-introState">
              <div className="lon-ico">🗓️</div>
              <h3>Build your regimen</h3>
              <p>Turn your ranked interventions into a regimen — a cited dose where an authority states one, otherwise your clinician sets it — and a clinical safety briefing built only from your safety checks and cited authority warnings, with questions to take to your provider.</p>
              <button className="lon-btn lon-btnPrimary" disabled={ranked.length === 0} onClick={() => triggerBrief()}>
                Build regimen &amp; briefing
                <span className="lon-freeChip">FREE</span>
              </button>
              {ranked.length === 0 && <div className="lon-introNote">Rank your interventions first.</div>}
            </div>
          )}

          {!briefingLoading && briefing && (
            <>
              {Array.isArray(briefing.regimen) && briefing.regimen.length > 0 && (
                <div className="lon-card lon-cardPad">
                  <div className="lon-timelineGroup">
                    <span className="lon-timelineCadence">🗓️ Regimen</span>
                    {briefing.regimen.map((r, i) => (
                      <div className="lon-tlItem" key={i}>
                        <div className="lon-tlRail"><div className="lon-tlDot" />{i < briefing.regimen.length - 1 && <div className="lon-tlLine" />}</div>
                        <div className="lon-tlBody">
                          <div className="lon-tlWhat">{r.item}</div>
                          {/* D7 (G16): the dose is never model-written — it is a cited amount, or
                              "Set by your clinician" / "Discuss the dose with your clinician". */}
                          {r.dose && <div className="lon-doseNote" data-testid="regimen-dose">Dose: {r.dose}</div>}
                          <LongevityEvidence
                            item={{ name: r.item, evidence_sources: r.sources, evidence_warnings: r.warnings }}
                            testId="regimen-citations"
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {((briefing.safety_briefing || []).length > 0 || (briefing.provider_talking_points || []).length > 0) && (
                <>
                  <h3 className="lon-blockTitle">Clinical safety briefing</h3>
                  <div className="lon-briefingModal">
                    <div className="lon-briefingHeader">
                      <span className="lon-ico">🛡️</span>
                      <h3>Clinical Safety Briefing</h3>
                    </div>
                    <div className="lon-briefingBody">
                      <div className="lon-briefBlock">
                        <h4>⚠️ Safety notes</h4>
                        {(briefing.safety_briefing || []).length === 0 && (
                          <p className="lon-introNote" data-testid="briefing-no-notes">
                            Your safety checks found nothing to flag for these items, and no authority warning is on file for them.
                          </p>
                        )}
                        {(briefing.safety_briefing || []).map((s, i) => {
                          const sev = (s.severity || '').toLowerCase();
                          const cls = sev.includes('avoid') ? 'avoid' : sev.includes('aesthet') ? 'aesthetic' : sev.includes('interact') ? 'interaction' : 'tell';
                          return (
                            <div className={`lon-briefLine ${cls}`} key={i} data-testid="briefing-line">
                              <span><b>{s.item}</b> — {s.mechanism}{s.guidance ? ` ${s.guidance}` : ''}</span>
                              {/* Apple 1.4.1 (F-4): every safety note carries ITS OWN source. */}
                              <CitationChip sources={s.sources || []} featureName="Longevity Intelligence"
                                            itemTitle={`${s.item} — safety note`} />
                            </div>
                          );
                        })}
                      </div>
                      {Array.isArray(briefing.provider_talking_points) && briefing.provider_talking_points.length > 0 && (
                        <div className="lon-briefBlock">
                          <h4>🗣️ What to tell your provider</h4>
                          <ul className="lon-ptpList">
                            {briefing.provider_talking_points.map((p, i) => <li key={i}>{typeof p === 'string' ? p : p.text || JSON.stringify(p)}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                    <div className="lon-briefFooter">
                      <span className="lon-disclaimer">This is educational information, not medical advice. Confirm all interventions — clinical and aesthetic — with your licensed provider.</span>
                      <button className="lon-btn lon-btnSecondary lon-btnSm" onClick={() => window.print()}>🖨️ Print</button>
                      <button className="lon-btn lon-btnPrimary lon-btnSm" onClick={() => setBriefing(null)}>Dismiss</button>
                    </div>
                  </div>
                </>
              )}
            </>
          )}
        </section>
      )}

      {/* ═══════════ HISTORY ═══════════ */}
      {tab === 'history' && (
        <section className="lon-panel" role="tabpanel">
          <h2 className="lon-blockTitle">Your biological age over time</h2>
          <p className="lon-blockSub">The closed loop made visible — your number responding to your data and behavior.</p>

          {assessment ? (
            <div className="lon-historyLog">
              <div className="lon-histItem">
                <div className="lon-histDate">{assessment.computed_at ? new Date(assessment.computed_at).toLocaleDateString() : 'Latest'}</div>
                <div className="lon-histDesc">
                  Current assessment · bio-age {fmtNum(assessment.biological_age)}
                  <span className="lon-histMeta"> · {assessment.method_version || 'PhenoAge'} · {assessment.confidence || 'Moderate'} confidence</span>
                </div>
                <div className="lon-histDelta down">{delta != null ? fmtNum(delta) : ''}</div>
              </div>
            </div>
          ) : (
            <div className="lon-introState">
              <div className="lon-ico">🕑</div>
              <h3>No history yet</h3>
              <p>Run your first assessment to start tracking your biological age over time.</p>
              <button className="lon-btn lon-btnPrimary" onClick={goAssess}>
                Assess my biological age <span className="lon-freeChip">FREE</span>
              </button>
            </div>
          )}

          {/* Recalibration settings — Pro = manual, Max = auto */}
          {!isMax ? (
            <div className="lon-recalSettings">
              <span style={{ fontSize: '22px' }}>🔄</span>
              <div className="lon-grow">
                <div className="lon-stitle">Manual recalibration</div>
                <div className="lon-sdesc">Re-assess on demand when your data changes. <b>Continuous auto-recalibration is a Longevity • Optimize feature.</b></div>
              </div>
              <button className="lon-btn lon-btnPrimary lon-btnSm" onClick={goAssess}>
                Re-assess <span className="lon-freeChip">FREE</span>
              </button>
            </div>
          ) : (
            <div className="lon-recalSettings">
              <div className="lon-toggle" />
              <div className="lon-grow">
                <div className="lon-stitle">Auto-recalibration: ON</div>
                <div className="lon-sdesc">We re-assess automatically when your labs, meds, or weight change — debounced to once daily. Detection is free; a full re-simulation is offered, not auto-charged.</div>
              </div>
            </div>
          )}

          {/* Aesthetic preference toggle */}
          <div className="lon-prefToggle">
            <div className="lon-toggle" />
            <span>Include aesthetic / cosmetic interventions in my rankings <span className="lon-muted">(default on — a personalization preference, not a gate)</span></span>
          </div>
        </section>
      )}

      {/* Global disclaimer */}
      <div className="lon-globalDisclaimer">
        <span className="lon-ico">ℹ️</span>
        <span>Longevity Intelligence provides educational, informational guidance only — it is <b>not medical advice, diagnosis, or treatment</b>. All interventions, clinical and aesthetic, must be confirmed with your licensed provider before you act on them.</span>
      </div>
      </div>{/* /page-scroll-content */}

      {/* Confirm modal — ≥4cr or low-balance */}
      {confirm && (
        <div className="lon-modalOverlay" onClick={() => setConfirm(null)}>
          <div className="lon-modalContent" onClick={(e) => e.stopPropagation()}>
            <div className="lon-modalHeader">
              <span style={{ fontSize: '22px' }}>⟡</span>
              <h3>{confirm.title}</h3>
            </div>
            <div className="lon-modalBody">
              <div className="lon-modalCostRow">
                <div className="lon-spend">{confirm.cost} <small>credits</small></div>
                <div className="lon-after">After this action<b>{creditsLeft != null ? `${Math.max(0, creditsLeft - confirm.cost)} left` : '—'}</b></div>
              </div>
              <p>This is a credit-spending action. Navigating and viewing your results afterward is always free.</p>
              <div className="lon-modalActions">
                <button className="lon-btn lon-btnSecondary lon-btnSm" onClick={() => setConfirm(null)}>Cancel</button>
                <button className="lon-btn lon-btnPrimary lon-btnSm" onClick={() => { const fn = confirm.onRun; setConfirm(null); fn(); }}>Confirm &amp; run</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
export default function LongevityPage({ fetchWithAuth, setActiveTab }) {
  const { user } = useContext(AuthContext);
  return (
    <PaidGate
      user={user}
      featureName="Longevity Intelligence"
      requiredTier="pro"
      onUpgradeClick={() => setActiveTab('account')}
    >
      <LongevityInner fetchWithAuth={fetchWithAuth} setActiveTab={setActiveTab} user={user} />
    </PaidGate>
  );
}
